// src/components/Footer.jsx
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ReactSVG } from "react-svg";

const withBase = (p) =>
  `${import.meta.env.BASE_URL}${String(p || "").replace(/^\//, "")}`;

/**
 * Props:
 *  - updatesCount: number badge on "Updates"
 *  - hiddenOn: array of route prefixes to hide footer on (e.g. ['/sign-in','/sign-up','/forgot-password'])
 */
const Footer = ({ updatesCount = 0, hiddenOn = [] }) => {
  const year = new Date().getFullYear();
  const { pathname } = useLocation();
  const shouldHide = hiddenOn.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  return (
    <footer className="app-footer" aria-hidden={shouldHide ? "true" : undefined}>
      <style>{`
        :root{
          --tabbar-h: 76px;
          --safe-b: env(safe-area-inset-bottom, 0px);
          --brand:#107b49;
          --brand-2:#0fb66e;
          --ink:#1f2937;
          --muted:#63779e;
          --ring:rgba(16,123,73,.14);
          --bar-bg: linear-gradient(180deg, rgba(240,251,246,.9) 0%, rgba(255,255,255,.96) 100%);
          --border:#e5efe7;
        }
        @media (prefers-color-scheme: dark){
          :root{
            --ink:#e5e7eb;
            --muted:#9fb2d2;
            --bar-bg: linear-gradient(180deg, rgba(17,24,39,.7) 0%, rgba(17,24,39,.6) 100%);
            --border: rgba(255,255,255,.08);
          }
        }

        .app-footer{ background:#fff; border-top:1px solid var(--border); }
        @media (prefers-color-scheme: dark){ .app-footer{ background:#0b1320; } }

        .footer-bottom{
          padding:10px 0; text-align:center; color:var(--muted); font-size:14px;
        }

        /* fixed app dock */
        .footer-nav-wrapper{
          position: fixed; left: 0; right: 0; bottom: 0;
          height: var(--tabbar-h);
          display: grid; grid-template-columns: repeat(4, 1fr);
          align-items: stretch;
          background: var(--bar-bg);
          border-top: 1px solid var(--border);
          border-radius: 18px 18px 0 0;
          box-shadow: 0 -8px 20px rgba(0,0,0,.08);
          z-index: 1030;
          padding: 6px 6px calc(8px + var(--safe-b));
          backdrop-filter: saturate(160%) blur(6px);
        }
        @media (min-width: 768px){
          .footer-nav-wrapper{ left:12px; right:12px; }
        }
        @media (max-width: 420px){
          .footer-nav-wrapper{ left:0; right:0; border-radius:12px 12px 0 0; }
        }
        @media (prefers-reduced-motion: reduce){
          .footer-nav-wrapper{ backdrop-filter: none; }
        }

        /* hide when requested */
        .app-footer[aria-hidden="true"] .footer-nav-wrapper,
        .app-footer[aria-hidden="true"] .tabbar-spacer{ display:none !important; }

        .tabbar-spacer{ height: calc(var(--tabbar-h) + var(--safe-b)); }

        .footer-nav-single{
          text-decoration: none; color: var(--ink);
          position: relative; border-radius: 12px; overflow: visible;
          transition: background .2s ease, box-shadow .2s ease, color .2s ease, border-color .2s ease, transform .12s ease;
        }

        /* TIGHTER icon–label spacing */
        .menu-wrapper{
          height: 100%;
          display: grid;
          grid-template-rows: auto auto; /* icon row + label row */
          align-items: center; justify-items: center;
          gap: 3px;                      /* was 6px */
          padding: 8px 6px 10px;
          min-width: 64px;
        }
        @media (max-width: 420px){
          .menu-wrapper{ gap: 2px; }     /* even tighter on very small phones */
        }

        .footer-nav-single svg{
          width: 24px; height: 24px;
          display: block;                /* remove inline baseline gap */
        }
        .menu-wrapper span{
          font-size: 12.5px; line-height: 1.15; white-space: nowrap;
        }

        .footer-nav-single:hover,
        .footer-nav-single:focus-visible{
          color:#0a5a39;
          background: linear-gradient(180deg, rgba(219,246,233,1) 0%, rgba(199,241,221,1) 100%);
          outline: none; border:1px solid rgba(16,123,73,.35);
          box-shadow: 0 1px 0 rgba(255,255,255,.85) inset, 0 0 0 4px rgba(16,123,73,.20);
          transform: translateY(-1px);
        }
        .footer-nav-single:hover .menu-wrapper span,
        .footer-nav-single:focus-visible .menu-wrapper span{ font-weight: 600; }
        .footer-nav-single:hover svg *, .footer-nav-single.is-active svg *{
          fill: currentColor !important; stroke: currentColor !important;
        }

        .footer-nav-single.is-active{
          color: var(--brand);
          background: linear-gradient(180deg, rgba(232,250,241,1) 0%, rgba(255,255,255,1) 100%);
          border: 1px solid rgba(16,123,73,.28);
          box-shadow: 0 1px 0 rgba(255,255,255,.9) inset, 0 4px 14px rgba(16,123,73,.12);
        }
        .footer-nav-single.is-active::before{
          content:""; position:absolute; top:0; left:18%; width:64%; height:4px;
          border-radius:999px; background: linear-gradient(90deg, var(--brand), var(--brand-2));
        }

        /* Updates badge */
        .footer-nav-single.has-updates .menu-wrapper::after{
          content: attr(data-updates);
          position: absolute; top: 6px; right: 12px;
          min-width: 18px; height: 18px; padding: 0 5px;
          display: inline-grid; place-items: center;
          font-size: 11px; line-height: 1; color: #fff;
          background: #ef4444; border-radius: 999px; box-shadow: 0 1px 0 rgba(0,0,0,.12);
        }
      `}</style>

      <div className="container">
        <div className="footer-bottom">© {year} Share My Venture</div>
      </div>

      {/* spacer prevents overlap with fixed bar */}
      <div className="tabbar-spacer" aria-hidden="true" />

      {/* fixed app dock */}
      <nav className="footer-nav-wrapper" aria-label="App Tab Bar" role="navigation">
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `footer-nav-single ${isActive ? "is-active" : ""}`}
          aria-label="Home"
        >
          <div className="menu-wrapper">
            <ReactSVG src={withBase("assets/img/icons/home.svg")} />
            <span>Home</span>
          </div>
        </NavLink>

        <NavLink
          to="/l/project"
          className={({ isActive }) => `footer-nav-single ${isActive ? "is-active" : ""}`}
          aria-label="Ideation"
        >
          <div className="menu-wrapper">
            <ReactSVG src={withBase("assets/img/icons/chat.svg")} />
            <span>Ideation</span>
          </div>
        </NavLink>

        <NavLink
          to="/f/project"
          className={({ isActive }) => `footer-nav-single ${isActive ? "is-active" : ""}`}
          aria-label="Funding"
        >
          <div className="menu-wrapper">
            <ReactSVG src={withBase("assets/img/icons/cart.svg")} />
            <span>Funding</span>
          </div>
        </NavLink>

        <NavLink
          to="/log"
          className={({ isActive }) =>
            `footer-nav-single ${isActive ? "is-active" : ""} ${updatesCount > 0 ? "has-updates" : ""}`
          }
          aria-label="Updates"
        >
          <div
            className="menu-wrapper"
            data-updates={updatesCount > 9 ? "9+" : updatesCount > 0 ? String(updatesCount) : undefined}
          >
            <ReactSVG src={withBase("assets/img/icons/store.svg")} />
            <span>Updates</span>
          </div>
        </NavLink>
      </nav>
    </footer>
  );
};

export default Footer;
